import api, { handleApiError } from './client';
import { USER_ROLES } from '../../utils/constants';

export const authService = {
  login: async (email, password) => {
    try {
      const response = await api.post('/api/auth/login', { email, password });
      const loginData = response.data;
      if (loginData?.token) {
        localStorage.setItem('authToken', loginData.token);
        localStorage.setItem('user', JSON.stringify(loginData.user));
        return { success: true, data: loginData };
      }
      return { success: false, message: 'Credenziali non valide' };
    } catch (error) {
      console.error('Login error:', error);
      return handleApiError(error, 'Errore di connessione al server');
    }
  },

  logout: () => {
    if (authService.isImpersonating()) {
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('user');
      try {
        window.close();
      } catch (_) {}
      window.location.href = '/login';
      return;
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },

  getCurrentUser: () => {
    // sessionStorage has priority: impersonated tabs store their data there
    const user =
      sessionStorage.getItem('user') || localStorage.getItem('user');
    if (!user) return null;
    try {
      return JSON.parse(user);
    } catch {
      return null;
    }
  },

  hasRole: (role) => {
    const currentUser = authService.getCurrentUser();
    return currentUser?.roles?.includes(role) || false;
  },

  hasAnyRole: (roles = []) => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser || roles.length === 0) {
      return false;
    }
    return roles.some((role) => currentUser.roles?.includes(role));
  },

  isAthleteOnly: () => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser || !currentUser.roles) return false;
    const isPortalRole =
      currentUser.roles.includes(USER_ROLES.ATHLETE) ||
      currentUser.roles.includes(USER_ROLES.PARENT);
    return isPortalRole && !currentUser.roles.includes(USER_ROLES.ADMIN);
  },

  getDefaultRedirectRoute: () => {
    return authService.isAthleteOnly() ? '/portal/profile' : '/dashboard';
  },

  isAuthenticated: () => {
    return !!(
      sessionStorage.getItem('authToken') || localStorage.getItem('authToken')
    );
  },

  /** Returns true if the current session is an admin impersonation session */
  isImpersonating: () => {
    return !!sessionStorage.getItem('authToken');
  },

  changePassword: async (passwordData) => {
    try {
      const response = await api.post('/api/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      return response.data;
    } catch (error) {
      console.error('Change password error:', error);
      return handleApiError(error, 'Errore nel cambio password');
    }
  },

  changeLoginEmail: async (newLoginEmail) => {
    try {
      const response = await api.post('/api/auth/change-login-email', { newLoginEmail });
      const result = response.data;
      if (result?.success) {
        if (result.token) {
          localStorage.setItem('authToken', result.token);
        }
        if (result.user) {
          const storedUser = localStorage.getItem('user');
          const currentStored = storedUser ? JSON.parse(storedUser) : {};
          localStorage.setItem('user', JSON.stringify({ ...currentStored, ...result.user }));
        }
        return { success: true, message: result.message, data: result.user };
      }
      return {
        success: false,
        message: result?.message || "Impossibile modificare l'email di login",
      };
    } catch (error) {
      console.error('Change login email error:', error);
      return handleApiError(error, 'Errore di connessione al server');
    }
  },

  requestPasswordReset: async (email, returnUrl = null) => {
    try {
      const response = await api.post('/api/auth/request-password-reset', {
        email,
        ...(returnUrl && { returnUrl }),
      });
      return response.data;
    } catch (error) {
      console.error('Request password reset error:', error);
      return handleApiError(error, 'Errore nella richiesta di reset password');
    }
  },

  resetPassword: async (token, newPassword) => {
    try {
      const response = await api.post('/api/auth/reset-password', {
        token,
        newPassword,
      });
      return response.data;
    } catch (error) {
      console.error('Reset password error:', error);
      return handleApiError(error, 'Errore nel reset della password');
    }
  },
};

export default authService;
